// ===============================================
// ⚙️ SUPPORT SETTINGS - CONFIGURACIÓN DEL SISTEMA
// ===============================================

import React, { useState, useEffect } from 'react';
import { 
  Settings,
  Save,
  RotateCcw,
  Bell,
  Clock,
  Users,
  MessageSquare,
  Mail,
  Phone,
  Globe,
  Shield,
  Database,
  Zap,
  AlertTriangle,
  CheckCircle,
  Info,
  Volume2,
  Smartphone,
  Monitor,
  FileText,
  Tag,
  Filter,
  Link,
  Key,
  Lock,
  Eye,
  EyeOff,
  Upload,
  Download,
  RefreshCw,
  Plus,
  Trash2,
  Edit3
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// ===============================================
// 🔧 INTERFACES
// ===============================================

interface SupportConfiguration {
  general: {
    system_name: string;
    default_language: 'es' | 'en' | 'pt';
    timezone: string;
    business_hours: {
      start: string;
      end: string;
      days: string[];
    };
    max_concurrent_chats: number;
    auto_assignment: boolean;
    enable_ai_assistant: boolean;
  };
  notifications: {
    email_alerts: boolean;
    sms_alerts: boolean;
    desktop_notifications: boolean;
    sound_notifications: boolean;
    new_ticket_alert: boolean;
    escalation_alerts: boolean;
    customer_satisfaction_alerts: boolean;
  };
  chat: {
    enable_live_chat: boolean;
    max_queue_size: number;
    estimated_wait_time_display: boolean;
    allow_file_uploads: boolean;
    max_file_size_mb: number;
    allowed_file_types: string[];
    chat_history_retention_days: number;
    enable_chat_ratings: boolean;
  };
  tickets: {
    auto_ticket_numbering: boolean;
    ticket_prefix: string;
    default_priority: 'low' | 'medium' | 'high' | 'critical';
    enable_sla: boolean;
    sla_response_times: {
      low: number;
      medium: number;
      high: number;
      critical: number;
    };
    auto_escalation: boolean;
    escalation_rules: EscalationRule[];
  };
  integrations: {
    sisweb_enabled: boolean;
    sisweb_api_key: string;
    claude_ai_enabled: boolean;
    claude_api_key: string;
    email_smtp: {
      host: string;
      port: number;
      username: string;
      password: string;
      use_tls: boolean;
    };
    webhook_url: string;
    enable_analytics: boolean;
  };
  security: {
    require_authentication: boolean;
    session_timeout_minutes: number;
    max_login_attempts: number;
    enable_audit_log: boolean;
    data_retention_days: number;
    encrypt_sensitive_data: boolean;
  };
}

interface EscalationRule {
  id: string;
  name: string;
  condition: string;
  action: string;
  target_user: string;
  delay_minutes: number;
  enabled: boolean;
}

interface SettingsSection {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
}

// ===============================================
// 📊 COMPONENTE PRINCIPAL
// ===============================================

const SupportSettings: React.FC<{ stats?: any; onStatsUpdate?: (stats: any) => void }> = ({ 
  stats, 
  onStatsUpdate 
}) => {
  const { isDark } = useTheme();
  const [config, setConfig] = useState<SupportConfiguration | null>(null);
  const [activeSection, setActiveSection] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // ===============================================
  // 🎨 CLASES DE ESTILO
  // ===============================================

  const cardClasses = `rounded-lg shadow-md transition-all duration-200 ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-800';
  const subTextClasses = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClasses = isDark ? 'border-gray-600' : 'border-gray-300';
  const inputClasses = `px-3 py-2 border rounded-md ${borderClasses} ${
    isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'
  }`;

  // ===============================================
  // 🔧 CONFIGURACIÓN DE SECCIONES
  // ===============================================

  const sections: SettingsSection[] = [
    {
      id: 'general',
      name: 'General',
      icon: Settings,
      description: 'Configuraciones básicas del sistema'
    },
    {
      id: 'notifications',
      name: 'Notificaciones',
      icon: Bell,
      description: 'Alertas y notificaciones'
    },
    {
      id: 'chat',
      name: 'Chat en Vivo',
      icon: MessageSquare,
      description: 'Configuración del sistema de chat'
    },
    {
      id: 'tickets',
      name: 'Tickets',
      icon: FileText,
      description: 'Gestión y configuración de tickets'
    },
    {
      id: 'integrations',
      name: 'Integraciones',
      icon: Link,
      description: 'APIs y servicios externos'
    },
    {
      id: 'security',
      name: 'Seguridad',
      icon: Shield,
      description: 'Configuraciones de seguridad y privacidad'
    }
  ];

  // ===============================================
  // 🔄 EFECTOS Y CARGA DE DATOS
  // ===============================================

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      // Simulación de configuración
      const mockConfig: SupportConfiguration = {
        general: {
          system_name: 'Sistema de Soporte Caterpillar',
          default_language: 'es',
          timezone: 'America/Santiago',
          business_hours: {
            start: '08:00',
            end: '18:00',
            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
          },
          max_concurrent_chats: 10,
          auto_assignment: true,
          enable_ai_assistant: true
        },
        notifications: {
          email_alerts: true,
          sms_alerts: false,
          desktop_notifications: true,
          sound_notifications: true,
          new_ticket_alert: true,
          escalation_alerts: true,
          customer_satisfaction_alerts: true
        },
        chat: {
          enable_live_chat: true,
          max_queue_size: 50,
          estimated_wait_time_display: true,
          allow_file_uploads: true,
          max_file_size_mb: 10,
          allowed_file_types: ['pdf', 'jpg', 'png', 'mp4', 'doc', 'docx'],
          chat_history_retention_days: 90,
          enable_chat_ratings: true
        },
        tickets: {
          auto_ticket_numbering: true,
          ticket_prefix: 'CAT',
          default_priority: 'medium',
          enable_sla: true,
          sla_response_times: {
            low: 24,
            medium: 8,
            high: 4,
            critical: 1
          },
          auto_escalation: true,
          escalation_rules: [
            {
              id: '1',
              name: 'Tickets críticos sin respuesta',
              condition: 'priority=critical AND status=open AND age>1hour',
              action: 'escalate_to_supervisor',
              target_user: 'supervisor@cat.com',
              delay_minutes: 60,
              enabled: true
            }
          ]
        },
        integrations: {
          sisweb_enabled: true,
          sisweb_api_key: '••••••••••••••••',
          claude_ai_enabled: true,
          claude_api_key: '••••••••••••••••',
          email_smtp: {
            host: 'smtp.empresa.com',
            port: 587,
            username: 'soporte@empresa.com',
            password: '••••••••',
            use_tls: true
          },
          webhook_url: 'https://api.empresa.com/webhooks/support',
          enable_analytics: true
        },
        security: {
          require_authentication: true,
          session_timeout_minutes: 480,
          max_login_attempts: 5,
          enable_audit_log: true,
          data_retention_days: 365,
          encrypt_sensitive_data: true
        }
      };

      setConfig(mockConfig);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config) return;
    
    setSaving(true);
    try {
      // Simular guardado
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setUnsavedChanges(false);
      
      // Mostrar notificación de éxito
      console.log('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error guardando configuración:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (confirm('¿Estás seguro de que quieres restaurar la configuración por defecto?')) {
      loadConfiguration();
      setUnsavedChanges(false);
    }
  };

  const updateConfig = (section: keyof SupportConfiguration, field: string, value: any) => {
    if (!config) return;
    
    setConfig(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [field]: value
      }
    }));
    setUnsavedChanges(true);
  };

  // ===============================================
  // 🎨 RENDER DE SECCIONES
  // ===============================================

  const renderGeneralSection = () => (
    <div className="space-y-6">
      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Nombre del Sistema
        </label>
        <input
          type="text"
          value={config?.general.system_name || ''}
          onChange={(e) => updateConfig('general', 'system_name', e.target.value)}
          className={`w-full ${inputClasses}`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Idioma por Defecto
        </label>
        <select
          value={config?.general.default_language || 'es'}
          onChange={(e) => updateConfig('general', 'default_language', e.target.value)}
          className={`w-full ${inputClasses}`}
        >
          <option value="es">🇪🇸 Español</option>
          <option value="en">🇺🇸 English</option>
          <option value="pt">🇧🇷 Português</option>
        </select>
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Zona Horaria
        </label>
        <select
          value={config?.general.timezone || 'America/Santiago'}
          onChange={(e) => updateConfig('general', 'timezone', e.target.value)}
          className={`w-full ${inputClasses}`}
        >
          <option value="America/Santiago">Santiago (GMT-3)</option>
          <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
          <option value="America/New_York">Nueva York (GMT-5)</option>
          <option value="Europe/Madrid">Madrid (GMT+1)</option>
        </select>
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Horario de Atención
        </label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-xs ${subTextClasses} mb-1`}>Hora Inicio</label>
            <input
              type="time"
              value={config?.general.business_hours.start || '08:00'}
              onChange={(e) => updateConfig('general', 'business_hours', {
                ...config?.general.business_hours,
                start: e.target.value
              })}
              className={inputClasses}
            />
          </div>
          <div>
            <label className={`block text-xs ${subTextClasses} mb-1`}>Hora Fin</label>
            <input
              type="time"
              value={config?.general.business_hours.end || '18:00'}
              onChange={(e) => updateConfig('general', 'business_hours', {
                ...config?.general.business_hours,
                end: e.target.value
              })}
              className={inputClasses}
            />
          </div>
        </div>
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Máximo de Chats Concurrentes
        </label>
        <input
          type="number"
          min="1"
          max="100"
          value={config?.general.max_concurrent_chats || 10}
          onChange={(e) => updateConfig('general', 'max_concurrent_chats', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="auto_assignment"
            checked={config?.general.auto_assignment || false}
            onChange={(e) => updateConfig('general', 'auto_assignment', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="auto_assignment" className={`text-sm ${textClasses}`}>
            Asignación automática de tickets
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="enable_ai_assistant"
            checked={config?.general.enable_ai_assistant || false}
            onChange={(e) => updateConfig('general', 'enable_ai_assistant', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="enable_ai_assistant" className={`text-sm ${textClasses}`}>
            Habilitar asistente de IA
          </label>
        </div>
      </div>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="space-y-4">
      <h3 className={`text-lg font-semibold ${textClasses} mb-4`}>
        Configuración de Notificaciones
      </h3>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm font-medium ${textClasses}`}>Alertas por Email</label>
            <p className={`text-xs ${subTextClasses}`}>Recibir notificaciones por correo</p>
          </div>
          <input
            type="checkbox"
            checked={config?.notifications.email_alerts || false}
            onChange={(e) => updateConfig('notifications', 'email_alerts', e.target.checked)}
            className="toggle-checkbox"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm font-medium ${textClasses}`}>Notificaciones SMS</label>
            <p className={`text-xs ${subTextClasses}`}>Mensajes de texto para casos urgentes</p>
          </div>
          <input
            type="checkbox"
            checked={config?.notifications.sms_alerts || false}
            onChange={(e) => updateConfig('notifications', 'sms_alerts', e.target.checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm font-medium ${textClasses}`}>Notificaciones de Escritorio</label>
            <p className={`text-xs ${subTextClasses}`}>Mostrar notificaciones en el navegador</p>
          </div>
          <input
            type="checkbox"
            checked={config?.notifications.desktop_notifications || false}
            onChange={(e) => updateConfig('notifications', 'desktop_notifications', e.target.checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm font-medium ${textClasses}`}>Sonidos de Notificación</label>
            <p className={`text-xs ${subTextClasses}`}>Reproducir sonido al recibir notificaciones</p>
          </div>
          <input
            type="checkbox"
            checked={config?.notifications.sound_notifications || false}
            onChange={(e) => updateConfig('notifications', 'sound_notifications', e.target.checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm font-medium ${textClasses}`}>Alertas de Nuevos Tickets</label>
            <p className={`text-xs ${subTextClasses}`}>Notificar cuando lleguen tickets nuevos</p>
          </div>
          <input
            type="checkbox"
            checked={config?.notifications.new_ticket_alert || false}
            onChange={(e) => updateConfig('notifications', 'new_ticket_alert', e.target.checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm font-medium ${textClasses}`}>Alertas de Escalamiento</label>
            <p className={`text-xs ${subTextClasses}`}>Notificar cuando se escalen tickets</p>
          </div>
          <input
            type="checkbox"
            checked={config?.notifications.escalation_alerts || false}
            onChange={(e) => updateConfig('notifications', 'escalation_alerts', e.target.checked)}
          />
        </div>
      </div>
    </div>
  );

  const renderChatSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <label className={`text-sm font-medium ${textClasses}`}>Habilitar Chat en Vivo</label>
          <p className={`text-xs ${subTextClasses}`}>Activar sistema de chat en tiempo real</p>
        </div>
        <input
          type="checkbox"
          checked={config?.chat.enable_live_chat || false}
          onChange={(e) => updateConfig('chat', 'enable_live_chat', e.target.checked)}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Tamaño Máximo de Cola
        </label>
        <input
          type="number"
          min="1"
          max="200"
          value={config?.chat.max_queue_size || 50}
          onChange={(e) => updateConfig('chat', 'max_queue_size', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
        <p className={`text-xs ${subTextClasses} mt-1`}>
          Número máximo de usuarios en cola de espera
        </p>
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Tamaño Máximo de Archivo (MB)
        </label>
        <input
          type="number"
          min="1"
          max="100"
          value={config?.chat.max_file_size_mb || 10}
          onChange={(e) => updateConfig('chat', 'max_file_size_mb', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Retención de Historial (días)
        </label>
        <input
          type="number"
          min="30"
          max="365"
          value={config?.chat.chat_history_retention_days || 90}
          onChange={(e) => updateConfig('chat', 'chat_history_retention_days', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="show_wait_time"
            checked={config?.chat.estimated_wait_time_display || false}
            onChange={(e) => updateConfig('chat', 'estimated_wait_time_display', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="show_wait_time" className={`text-sm ${textClasses}`}>
            Mostrar tiempo estimado de espera
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="allow_uploads"
            checked={config?.chat.allow_file_uploads || false}
            onChange={(e) => updateConfig('chat', 'allow_file_uploads', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="allow_uploads" className={`text-sm ${textClasses}`}>
            Permitir carga de archivos
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="enable_ratings"
            checked={config?.chat.enable_chat_ratings || false}
            onChange={(e) => updateConfig('chat', 'enable_chat_ratings', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="enable_ratings" className={`text-sm ${textClasses}`}>
            Habilitar calificaciones de chat
          </label>
        </div>
      </div>
    </div>
  );

  const renderTicketsSection = () => (
    <div className="space-y-6">
      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Prefijo de Tickets
        </label>
        <input
          type="text"
          maxLength="5"
          value={config?.tickets.ticket_prefix || 'CAT'}
          onChange={(e) => updateConfig('tickets', 'ticket_prefix', e.target.value.toUpperCase())}
          className={`w-full ${inputClasses}`}
          placeholder="CAT"
        />
        <p className={`text-xs ${subTextClasses} mt-1`}>
          Ejemplo: CAT-2024-001
        </p>
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Prioridad por Defecto
        </label>
        <select
          value={config?.tickets.default_priority || 'medium'}
          onChange={(e) => updateConfig('tickets', 'default_priority', e.target.value)}
          className={`w-full ${inputClasses}`}
        >
          <option value="low">Baja</option>
          <option value="medium">Media</option>
          <option value="high">Alta</option>
          <option value="critical">Crítica</option>
        </select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="auto_numbering"
            checked={config?.tickets.auto_ticket_numbering || false}
            onChange={(e) => updateConfig('tickets', 'auto_ticket_numbering', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="auto_numbering" className={`text-sm ${textClasses}`}>
            Numeración automática de tickets
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="enable_sla"
            checked={config?.tickets.enable_sla || false}
            onChange={(e) => updateConfig('tickets', 'enable_sla', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="enable_sla" className={`text-sm ${textClasses}`}>
            Habilitar SLA (Acuerdos de Nivel de Servicio)
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="auto_escalation"
            checked={config?.tickets.auto_escalation || false}
            onChange={(e) => updateConfig('tickets', 'auto_escalation', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="auto_escalation" className={`text-sm ${textClasses}`}>
            Escalamiento automático
          </label>
        </div>
      </div>

      {config?.tickets.enable_sla && (
        <div>
          <h4 className={`text-sm font-medium ${textClasses} mb-3`}>
            Tiempos de Respuesta SLA (horas)
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-xs ${subTextClasses} mb-1`}>Baja</label>
              <input
                type="number"
                min="1"
                value={config?.tickets.sla_response_times.low || 24}
                onChange={(e) => updateConfig('tickets', 'sla_response_times', {
                  ...config?.tickets.sla_response_times,
                  low: parseInt(e.target.value)
                })}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={`block text-xs ${subTextClasses} mb-1`}>Media</label>
              <input
                type="number"
                min="1"
                value={config?.tickets.sla_response_times.medium || 8}
                onChange={(e) => updateConfig('tickets', 'sla_response_times', {
                  ...config?.tickets.sla_response_times,
                  medium: parseInt(e.target.value)
                })}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={`block text-xs ${subTextClasses} mb-1`}>Alta</label>
              <input
                type="number"
                min="1"
                value={config?.tickets.sla_response_times.high || 4}
                onChange={(e) => updateConfig('tickets', 'sla_response_times', {
                  ...config?.tickets.sla_response_times,
                  high: parseInt(e.target.value)
                })}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={`block text-xs ${subTextClasses} mb-1`}>Crítica</label>
              <input
                type="number"
                min="1"
                value={config?.tickets.sla_response_times.critical || 1}
                onChange={(e) => updateConfig('tickets', 'sla_response_times', {
                  ...config?.tickets.sla_response_times,
                  critical: parseInt(e.target.value)
                })}
                className={inputClasses}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderIntegrationsSection = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className={`text-sm font-medium ${textClasses}`}>SISWeb Integration</h4>
        
        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm ${textClasses}`}>Habilitar SISWeb</label>
            <p className={`text-xs ${subTextClasses}`}>Integración con sistema SISWeb</p>
          </div>
          <input
            type="checkbox"
            checked={config?.integrations.sisweb_enabled || false}
            onChange={(e) => updateConfig('integrations', 'sisweb_enabled', e.target.checked)}
          />
        </div>

        {config?.integrations.sisweb_enabled && (
          <div>
            <label className={`block text-sm font-medium ${textClasses} mb-2`}>
              API Key SISWeb
            </label>
            <div className="relative">
              <input
                type={showPasswords ? 'text' : 'password'}
                value={config?.integrations.sisweb_api_key || ''}
                onChange={(e) => updateConfig('integrations', 'sisweb_api_key', e.target.value)}
                className={`w-full pr-10 ${inputClasses}`}
                placeholder="Ingresa tu API key"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(!showPasswords)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h4 className={`text-sm font-medium ${textClasses}`}>Claude AI Integration</h4>
        
        <div className="flex items-center justify-between">
          <div>
            <label className={`text-sm ${textClasses}`}>Habilitar Claude AI</label>
            <p className={`text-xs ${subTextClasses}`}>Asistente de IA para soporte</p>
          </div>
          <input
            type="checkbox"
            checked={config?.integrations.claude_ai_enabled || false}
            onChange={(e) => updateConfig('integrations', 'claude_ai_enabled', e.target.checked)}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h4 className={`text-sm font-medium ${textClasses}`}>Configuración SMTP</h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${textClasses} mb-2`}>Host SMTP</label>
            <input
              type="text"
              value={config?.integrations.email_smtp.host || ''}
              onChange={(e) => updateConfig('integrations', 'email_smtp', {
                ...config?.integrations.email_smtp,
                host: e.target.value
              })}
              className={`w-full ${inputClasses}`}
              placeholder="smtp.empresa.com"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${textClasses} mb-2`}>Puerto</label>
            <input
              type="number"
              value={config?.integrations.email_smtp.port || 587}
              onChange={(e) => updateConfig('integrations', 'email_smtp', {
                ...config?.integrations.email_smtp,
                port: parseInt(e.target.value)
              })}
              className={`w-full ${inputClasses}`}
            />
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClasses} mb-2`}>Usuario SMTP</label>
          <input
            type="text"
            value={config?.integrations.email_smtp.username || ''}
            onChange={(e) => updateConfig('integrations', 'email_smtp', {
              ...config?.integrations.email_smtp,
              username: e.target.value
            })}
            className={`w-full ${inputClasses}`}
            placeholder="usuario@empresa.com"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="use_tls"
            checked={config?.integrations.email_smtp.use_tls || false}
            onChange={(e) => updateConfig('integrations', 'email_smtp', {
              ...config?.integrations.email_smtp,
              use_tls: e.target.checked
            })}
            className="mr-3"
          />
          <label htmlFor="use_tls" className={`text-sm ${textClasses}`}>
            Usar TLS/SSL
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <label className={`text-sm font-medium ${textClasses}`}>Requerir Autenticación</label>
          <p className={`text-xs ${subTextClasses}`}>Los usuarios deben autenticarse para acceder</p>
        </div>
        <input
          type="checkbox"
          checked={config?.security.require_authentication || false}
          onChange={(e) => updateConfig('security', 'require_authentication', e.target.checked)}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Tiempo de Sesión (minutos)
        </label>
        <input
          type="number"
          min="30"
          max="1440"
          value={config?.security.session_timeout_minutes || 480}
          onChange={(e) => updateConfig('security', 'session_timeout_minutes', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
        <p className={`text-xs ${subTextClasses} mt-1`}>
          Tiempo antes de cerrar sesión automáticamente
        </p>
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Máximo Intentos de Login
        </label>
        <input
          type="number"
          min="3"
          max="10"
          value={config?.security.max_login_attempts || 5}
          onChange={(e) => updateConfig('security', 'max_login_attempts', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${textClasses} mb-2`}>
          Retención de Datos (días)
        </label>
        <input
          type="number"
          min="90"
          max="2555" // ~7 años
          value={config?.security.data_retention_days || 365}
          onChange={(e) => updateConfig('security', 'data_retention_days', parseInt(e.target.value))}
          className={`w-full ${inputClasses}`}
        />
        <p className={`text-xs ${subTextClasses} mt-1`}>
          Tiempo que se conservan los datos antes de eliminarlos
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enable_audit"
            checked={config?.security.enable_audit_log || false}
            onChange={(e) => updateConfig('security', 'enable_audit_log', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="enable_audit" className={`text-sm ${textClasses}`}>
            Habilitar registro de auditoría
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="encrypt_data"
            checked={config?.security.encrypt_sensitive_data || false}
            onChange={(e) => updateConfig('security', 'encrypt_sensitive_data', e.target.checked)}
            className="mr-3"
          />
          <label htmlFor="encrypt_data" className={`text-sm ${textClasses}`}>
            Encriptar datos sensibles
          </label>
        </div>
      </div>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'general': return renderGeneralSection();
      case 'notifications': return renderNotificationsSection();
      case 'chat': return renderChatSection();
      case 'tickets': return renderTicketsSection();
      case 'integrations': return renderIntegrationsSection();
      case 'security': return renderSecuritySection();
      default: return null;
    }
  };

  // ===============================================
  // 🎨 RENDER PRINCIPAL
  // ===============================================

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center space-x-3">
          <Settings className="w-6 h-6 animate-spin text-blue-600" />
          <span className={textClasses}>Cargando configuración...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* ✅ HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${textClasses}`}>
            Configuración del Sistema
          </h2>
          <p className={subTextClasses}>
            Personaliza el comportamiento del sistema de soporte
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {unsavedChanges && (
            <span className="text-orange-600 text-sm font-medium">
              Cambios sin guardar
            </span>
          )}
          
          <button
            onClick={resetToDefaults}
            className={`px-4 py-2 rounded-md border ${borderClasses} ${
              isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restaurar
          </button>
          
          <button
            onClick={saveConfiguration}
            disabled={saving || !unsavedChanges}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md font-medium flex items-center"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* ✅ NAVEGACIÓN LATERAL */}
        <div className="lg:col-span-1">
          <div className={cardClasses}>
            <div className="p-4">
              <h3 className={`text-sm font-semibold ${textClasses} mb-3`}>
                Secciones
              </h3>
              
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                          : `${subTextClasses} hover:bg-gray-100 dark:hover:bg-gray-700`
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      {section.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* ✅ CONTENIDO PRINCIPAL */}
        <div className="lg:col-span-3">
          <div className={cardClasses}>
            <div className="p-6">
              {/* Header de sección */}
              <div className="mb-6">
                <h3 className={`text-lg font-semibold ${textClasses} mb-2`}>
                  {sections.find(s => s.id === activeSection)?.name}
                </h3>
                <p className={subTextClasses}>
                  {sections.find(s => s.id === activeSection)?.description}
                </p>
              </div>

              {/* Contenido de la sección */}
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportSettings;